import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  useThreatStatusesQuery,
  useServicesQuery,
  useSourcesQuery,
} from '../queries/lookups';
import {
  setStatusFilterValues,
  setServicesData,
  setSourcesData,
  setPickerStartDate,
  setPickerEndDate,
  setCompanyFilter,
  setExcludeCompany,
  setProtectedAssetsInclude,
  setProtectedAssetsExclude,
  setReviewStatusFilterValues,
  setBrpVerdictFilter,
  setBrpConfidenceFilter,
} from '../store/filter/action';
import {
  defaultStatusFilter,
  defaultServiceFilter,
  defaultSourcesFilter,
  defaultStartDate,
  defaultEndDate,
  defaultReviewStatusFilter,
  defaultSortBy,
  defaultSortOrder,
  buildSourceOptions,
  getDateOrDefault,
  BRP_VERDICT_OPTIONS,
  BRP_CONFIDENCE_OPTIONS,
} from '../utils';
import {
  serializeCompanyParam,
  parseCompanyParam,
  serializeDateToParams,
  parseDateFromParams,
  parseSortFromParams,
} from './urlParamUtils';

export interface SortConfig {
  sortBy: string;
  sortOrder: string;
}

interface UseFilterUrlSyncOptions {
  isCustomerReviewPage: boolean;
  sortConfig?: SortConfig;
  setSortConfig?: (config: SortConfig) => void;
  // Company filter child-expansion toggle (WHEEL-376) — Home-local state
  // synced through the `includeChildren` URL param, like sortConfig.
  includeChildCompanies?: boolean;
  setIncludeChildCompanies?: (value: boolean) => void;
}

interface FilterItem {
  id: number;
  label: string;
}

interface SourceItem {
  label: string;
  value: string;
}

const arraysMatch = <T>(a: T[], b: T[], key: keyof T): boolean => {
  if (a.length !== b.length) return false;
  const aKeys = a.map(item => item[key]).sort();
  const bKeys = b.map(item => item[key]).sort();
  return aKeys.every((val, i) => val === bKeys[i]);
};

function useFilterUrlSync(options: UseFilterUrlSyncOptions): {
  isInitializedFromUrl: boolean;
} {
  const {
    isCustomerReviewPage,
    sortConfig,
    setSortConfig,
    includeChildCompanies,
    setIncludeChildCompanies,
  } = options;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitializedFromUrl, setIsInitializedFromUrl] = useState(false);
  const isUpdatingUrl = useRef(false);
  const isApplyingFromUrl = useRef(false);
  const previousSearchRef = useRef<string>('');
  const isFirstSync = useRef(true);

  const filterState = useSelector((state: any) => state.filterList);
  const {
    statusFilterData,
    getServicesData: servicesData,
    getSourcesData: sourcesData,
    companyFilterData,
    companysExclude,
    protectedAssetsInclude = [],
    protectedAssetsExclude = [],
    getPickerStartDate: pickerStartDate,
    getPickerEndDate: pickerEndDate,
    reviewStatusFilterData,
    // Default to [] — these keys are absent from older redux-persist state.
    brpVerdictFilterData = [],
    brpConfidenceFilterData = [],
  } = filterState;

  const { data: statusList } = useThreatStatusesQuery();
  const { data: serviceList } = useServicesQuery();
  const {
    data: sourceList,
    isSuccess: sourcesLoaded,
    isError: sourcesFailed,
  } = useSourcesQuery();
  // On API failure buildSourceOptions falls back to the static list, so a
  // settled query (success or error) is enough to parse the source param.
  const sourcesSettled = sourcesLoaded || sourcesFailed;

  const getDefaultStatusFilter = useCallback((): FilterItem[] => {
    return isCustomerReviewPage
      ? defaultReviewStatusFilter
      : defaultStatusFilter;
  }, [isCustomerReviewPage]);

  const getActiveStatusData = useCallback((): FilterItem[] => {
    return isCustomerReviewPage ? reviewStatusFilterData : statusFilterData;
  }, [isCustomerReviewPage, reviewStatusFilterData, statusFilterData]);

  const serializeFiltersToParams = useCallback((): URLSearchParams => {
    const params = new URLSearchParams();
    const activeStatusData = getActiveStatusData();
    const defaultStatus = getDefaultStatusFilter();

    if (
      activeStatusData.length > 0 &&
      !arraysMatch(activeStatusData, defaultStatus, 'label')
    ) {
      params.set(
        'status',
        activeStatusData.map((s: FilterItem) => s.label).join(','),
      );
    }

    if (
      servicesData.length > 0 &&
      !arraysMatch(servicesData, defaultServiceFilter, 'id')
    ) {
      params.set(
        'service',
        servicesData.map((s: FilterItem) => String(s.id)).join(','),
      );
    }

    if (
      sourcesData.length > 0 &&
      !arraysMatch(sourcesData, defaultSourcesFilter, 'value')
    ) {
      params.set(
        'source',
        sourcesData.map((s: SourceItem) => s.value).join(','),
      );
    }

    if (brpVerdictFilterData.length > 0) {
      params.set(
        'brpVerdict',
        brpVerdictFilterData.map((v: SourceItem) => v.value).join(','),
      );
    }

    if (brpConfidenceFilterData.length > 0) {
      params.set(
        'brpConfidence',
        brpConfidenceFilterData.map((c: SourceItem) => c.value).join(','),
      );
    }

    if (companyFilterData.length > 0) {
      params.set('company', serializeCompanyParam(companyFilterData));
    }

    if (companysExclude.length > 0) {
      params.set('excludeCompany', serializeCompanyParam(companysExclude));
    }

    if (includeChildCompanies) {
      params.set('includeChildren', '1');
    }

    if (protectedAssetsInclude.length > 0) {
      params.set('asset', protectedAssetsInclude.join(','));
    }

    if (protectedAssetsExclude.length > 0) {
      params.set('excludeAsset', protectedAssetsExclude.join(','));
    }

    const resolvedStart = getDateOrDefault(pickerStartDate, defaultStartDate);
    const resolvedEnd = getDateOrDefault(pickerEndDate, defaultEndDate);

    const dateParams = serializeDateToParams(
      resolvedStart,
      resolvedEnd,
      defaultStartDate,
      defaultEndDate,
    );
    Object.entries(dateParams).forEach(([key, value]) => {
      params.set(key, value);
    });

    if (
      sortConfig &&
      (sortConfig.sortBy !== defaultSortBy ||
        sortConfig.sortOrder !== defaultSortOrder)
    ) {
      params.set('sort', sortConfig.sortBy);
      params.set('order', sortConfig.sortOrder);
    }

    return params;
  }, [
    getActiveStatusData,
    getDefaultStatusFilter,
    servicesData,
    sourcesData,
    brpVerdictFilterData,
    brpConfidenceFilterData,
    companyFilterData,
    companysExclude,
    includeChildCompanies,
    protectedAssetsInclude,
    protectedAssetsExclude,
    pickerStartDate,
    pickerEndDate,
    sortConfig,
  ]);

  const applyUrlToRedux = useCallback((): boolean => {
    const params = new URLSearchParams(location.search);
    const hasAnyParam =
      params.has('status') ||
      params.has('service') ||
      params.has('source') ||
      params.has('brpVerdict') ||
      params.has('brpConfidence') ||
      params.has('company') ||
      params.has('excludeCompany') ||
      params.has('includeChildren') ||
      params.has('asset') ||
      params.has('excludeAsset') ||
      params.has('dateRange') ||
      params.has('startDate') ||
      params.has('endDate') ||
      params.has('sort') ||
      params.has('order');

    if (!hasAnyParam) {
      return false;
    }

    isApplyingFromUrl.current = true;

    const statusParam = params.get('status');
    if (statusParam) {
      const labels = statusParam.split(',').filter(Boolean);
      const resolved: FilterItem[] = [];
      for (const label of labels) {
        const found = statusList?.find(
          (s: { name: string; id: number }) => s.name === label,
        );
        if (found) {
          resolved.push({ id: found.id, label: found.name });
        }
      }
      if (resolved.length > 0) {
        if (isCustomerReviewPage) {
          dispatch(setReviewStatusFilterValues(resolved));
        } else {
          dispatch(setStatusFilterValues(resolved));
        }
      }
    } else {
      const defaultStatus = getDefaultStatusFilter();
      if (isCustomerReviewPage) {
        dispatch(setReviewStatusFilterValues(defaultStatus));
      } else {
        dispatch(setStatusFilterValues(defaultStatus));
      }
    }

    const serviceParam = params.get('service');
    if (serviceParam) {
      const ids = serviceParam
        .split(',')
        .filter(Boolean)
        .map(Number)
        .filter(id => !isNaN(id));
      const resolved: FilterItem[] = [];
      for (const id of ids) {
        const found = serviceList?.find(
          (s: { id: number; name: string }) => s.id === id,
        );
        if (found) {
          resolved.push({ id: found.id, label: found.name });
        }
      }
      if (resolved.length > 0) {
        dispatch(setServicesData(resolved));
      }
    } else {
      dispatch(setServicesData(defaultServiceFilter));
    }

    const sourceParam = params.get('source');
    if (sourceParam) {
      const sourceOptions = buildSourceOptions(sourceList);
      const values = sourceParam.split(',').filter(Boolean);
      const resolved: SourceItem[] = [];
      for (const value of values) {
        const found = sourceOptions.find(s => s.value === value);
        if (found) {
          resolved.push(found);
        }
      }
      // Reset to default when nothing resolves - keeping the session's last
      // selection would make a bad link silently show unrelated sources.
      dispatch(
        setSourcesData(resolved.length > 0 ? resolved : defaultSourcesFilter),
      );
    } else {
      dispatch(setSourcesData(defaultSourcesFilter));
    }

    const brpVerdictParam = params.get('brpVerdict');
    if (brpVerdictParam) {
      const values = brpVerdictParam.split(',').filter(Boolean);
      const resolved = BRP_VERDICT_OPTIONS.filter(o =>
        values.includes(o.value),
      );
      dispatch(setBrpVerdictFilter(resolved));
    } else {
      dispatch(setBrpVerdictFilter([]));
    }

    const brpConfidenceParam = params.get('brpConfidence');
    if (brpConfidenceParam) {
      const values = brpConfidenceParam.split(',').filter(Boolean);
      const resolved = BRP_CONFIDENCE_OPTIONS.filter(o =>
        values.includes(o.value),
      );
      dispatch(setBrpConfidenceFilter(resolved));
    } else {
      dispatch(setBrpConfidenceFilter([]));
    }

    const companyParam = params.get('company');
    if (companyParam) {
      dispatch(setCompanyFilter(parseCompanyParam(companyParam)));
    } else {
      dispatch(setCompanyFilter([]));
    }

    const excludeCompanyParam = params.get('excludeCompany');
    if (excludeCompanyParam) {
      dispatch(setExcludeCompany(parseCompanyParam(excludeCompanyParam)));
    } else {
      dispatch(setExcludeCompany([]));
    }

    if (setIncludeChildCompanies) {
      setIncludeChildCompanies(params.get('includeChildren') === '1');
    }

    const assetParam = params.get('asset');
    dispatch(
      setProtectedAssetsInclude(
        assetParam ? assetParam.split(',').filter(Boolean) : [],
      ),
    );

    const excludeAssetParam = params.get('excludeAsset');
    dispatch(
      setProtectedAssetsExclude(
        excludeAssetParam ? excludeAssetParam.split(',').filter(Boolean) : [],
      ),
    );

    const dates = parseDateFromParams(params, defaultStartDate, defaultEndDate);
    dispatch(setPickerStartDate(dates.startDate));
    dispatch(setPickerEndDate(dates.endDate));

    if (setSortConfig) {
      const parsedSort = parseSortFromParams(params);
      const nextSort = parsedSort ?? {
        sortBy: defaultSortBy,
        sortOrder: defaultSortOrder,
      };
      if (
        !sortConfig ||
        sortConfig.sortBy !== nextSort.sortBy ||
        sortConfig.sortOrder !== nextSort.sortOrder
      ) {
        setSortConfig(nextSort);
      }
    }

    queueMicrotask(() => {
      isApplyingFromUrl.current = false;
    });

    return true;
  }, [
    location.search,
    statusList,
    serviceList,
    sourceList,
    isCustomerReviewPage,
    dispatch,
    getDefaultStatusFilter,
    setSortConfig,
    setIncludeChildCompanies,
    sortConfig,
  ]);

  useEffect(() => {
    if (isInitializedFromUrl) return;

    const hasUrlParams = location.search.length > 1;
    const hasReferenceData =
      (statusList?.length ?? 0) > 0 &&
      (serviceList?.length ?? 0) > 0 &&
      sourcesSettled;

    if (hasUrlParams && !hasReferenceData) {
      return;
    }

    if (hasUrlParams && hasReferenceData) {
      applyUrlToRedux();
    }

    previousSearchRef.current = location.search;
    setIsInitializedFromUrl(true);
  }, [
    location.search,
    statusList,
    serviceList,
    sourcesSettled,
    isInitializedFromUrl,
    applyUrlToRedux,
  ]);

  useEffect(() => {
    if (!isInitializedFromUrl) return;

    const currentSearch = location.search;
    if (currentSearch !== previousSearchRef.current && !isUpdatingUrl.current) {
      const hasReferenceData =
        (statusList?.length ?? 0) > 0 &&
        (serviceList?.length ?? 0) > 0 &&
        sourcesSettled;
      if (hasReferenceData) {
        applyUrlToRedux();
      }
      previousSearchRef.current = currentSearch;
    }
  }, [
    location.search,
    isInitializedFromUrl,
    statusList,
    serviceList,
    sourcesSettled,
    applyUrlToRedux,
  ]);

  useEffect(() => {
    if (!isInitializedFromUrl) return;
    if (isApplyingFromUrl.current) return;

    const params = serializeFiltersToParams();
    const newSearch = params.toString() ? `?${params.toString()}` : '';
    const currentSearch = location.search || '';

    if (newSearch !== currentSearch) {
      isUpdatingUrl.current = true;
      navigate(
        { pathname: location.pathname, search: newSearch },
        { replace: isFirstSync.current },
      );
      isFirstSync.current = false;
      previousSearchRef.current = newSearch;
      queueMicrotask(() => {
        isUpdatingUrl.current = false;
      });
    }
  }, [
    isInitializedFromUrl,
    serializeFiltersToParams,
    navigate,
    location.pathname,
    location.search,
  ]);

  return { isInitializedFromUrl };
}

export default useFilterUrlSync;
